import { z } from "zod";

import { NPIResult } from "@/src/lib/npi";
import { properCase } from "@/src/lib/string";
import { validateObject, ObjectErrors } from "@/src/lib/utils/object-utils";
import { npiNumberSchema } from "@/src/models/common-schema";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { getPersonName, personSchema } from "@/src/models/person";

export enum PractitionerRole {
  ORDERING_PROVIDER = "Ordering Provider",
  PRIMARY_CARE_PROVIDER = "Primary Care Provider",
  RECEIVING_PROVIDER = "Receiving Provider",
  REFERRING_PROVIDER = "Referring Provider",
  UNKNOWN_PROVIDER = "Unknown Role",
}

export const practitionerSchema = z.object({
  ...personSchema.shape,
  npiNumber: npiNumberSchema.nullable(),
  specialty: z.string().nullable(),
  role: z.nativeEnum(PractitionerRole).describe(
    `
      The role of the practitioner in the referral typically.
      A receiving provider is the provider who will be treating the patient.
      A referring provider is the provider who is referring the patient to another provider.
      A primary care provider is a provider who is responsible for the overall care of the patient.
      A ordering provider is a provider who is ordering a service for the patient.
      `
  ),
}).describe(`
  When extracting provider information, follow these steps in order:

  1. First, look for these specific sections:
     - For receiving providers: Search near "To Provider", "Referred To", "Referring To"
     - For referring providers: Search near "From Provider", "Referred From", or "Referring From"

  2. If not found in step 1, then look for:
     - Provider name and details near the top of the document
     - Provider information in the header or footer
     - Provider details in signature blocks

  3. For each provider found, extract:
     - Full name (including any titles/credentials)
     - NPI number if present
     - Specialty if mentioned
     - Contact information if available

  Note: Always prioritize information found in the "To Provider" and "From Provider" sections as they are most likely to be accurate.
`);

export type Practitioner = z.infer<typeof practitionerSchema> & {
  npiRegistryEntry: NPIResult | null;
  displayId: string | null;
  ehrId: string | null;
  secondaryEhrId: string | null;
  isApproved: boolean | null;
  isDuplicate: boolean | null;
} & DatabaseDefaults;

export const practitionerShape: Practitioner = {
  id: "",
  givenName: null,
  familyName: null,
  middleName: null,
  preferredName: null,
  groupId: "",
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  npiNumber: null,
  specialty: null,
  prefix: [],
  suffix: [],
  role: PractitionerRole.UNKNOWN_PROVIDER,
  npiRegistryEntry: null,
  displayId: null,
  secondaryEhrId: null,
  ehrId: null,
  isApproved: null,
  isDuplicate: null,
};

export const isEmptyPractitioner = (practitioner: Practitioner) => {
  return (
    !practitioner ||
    (!practitioner.givenName &&
      !practitioner.familyName &&
      !practitioner.middleName &&
      !practitioner.preferredName &&
      !practitioner.npiNumber)
  );
};

export type PractitionerErrors = ObjectErrors<Practitioner>;

export function getProviderDisplayName(practitioner: Practitioner) {
  if (!practitioner) {
    return "";
  }

  const name = [practitioner.givenName, practitioner.familyName].filter(Boolean).join(" ");
  const specialty = practitioner.specialty ? ` (${practitioner.specialty})` : "";
  const state = practitioner.npiRegistryEntry?.addresses?.[0]?.state
    ? ` - ${practitioner.npiRegistryEntry.addresses[0].state}`
    : "";

  const npi = practitioner.npiNumber ? ` - ${practitioner.npiNumber}` : "";

  return `${name}${specialty}${state}${npi}`.trim();
}

export function getProviderNameWithCredentials(provider: Practitioner | null | undefined): string {
  if (!provider) {
    return "N/A";
  }

  const { givenName, middleName, familyName, npiRegistryEntry } = provider;

  const credential = npiRegistryEntry?.basic?.credential?.toUpperCase();

  return [properCase(givenName), properCase(middleName), properCase(familyName), credential].filter(Boolean).join(" ");
}

/**
 * Validates a Practitioner object using the generic validateObject helper.
 * If fieldsToValidate is provided, only those fields are validated.
 */
export function validatePractitioner(practitioner: Practitioner, fieldsToValidate?: (keyof Practitioner)[]) {
  return validateObject<Practitioner>(practitionerSchema, practitioner, fieldsToValidate);
}

export function getPractitionerNameWithCredentials(practitioner: Practitioner) {
  const nameParts = getPersonName(practitioner);

  const credential = practitioner.npiRegistryEntry?.basic?.credential?.toUpperCase();

  return [nameParts, credential].filter(Boolean).join(" ");
}
